/**
 * O que faz: Captura cada página nativa do relatório e monta um PDF multipágina.
 * Onde usa: useDashboardExport ao baixar os formatos A4 ou Mobile.
 * Conexões: html2canvas, jsPDF e dashboardReportFormats.
 * Tabelas: Nenhuma.
 * Data: 27/07/2026.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DASHBOARD_REPORT_FORMATS, type DashboardReportFormat } from './dashboardReportFormats';

interface RenderDashboardPdfInput {
  sourceElement: HTMLElement;
  format: DashboardReportFormat;
  fileName: string;
}

export const renderDashboardPdf = async ({ sourceElement, format, fileName }: RenderDashboardPdfInput) => {
  const reportFormat = DASHBOARD_REPORT_FORMATS[format];

  // O preview mantém a origem fora da tela; alguns navegadores retornam um canvas branco ao medir um alvo em coordenada negativa.
  // Esta cópia temporária fica em (0, 0) apenas durante a captura, sem interferir no modal aberto.
  const captureSource = sourceElement.cloneNode(true) as HTMLElement;
  captureSource.removeAttribute('aria-hidden');
  Object.assign(captureSource.style, {
    position: 'fixed',
    left: '0',
    top: '0',
    width: `${reportFormat.pixelWidth}px`,
    pointerEvents: 'none',
    zIndex: '-1',
  });
  document.body.appendChild(captureSource);

  const pages = Array.from(captureSource.querySelectorAll<HTMLElement>('[data-dashboard-report-page]'));
  if (pages.length === 0) {
    captureSource.remove();
    throw new Error('Nenhuma página do relatório foi encontrada');
  }

  // Aguarda as fontes da Agência Gens para que o canvas preserve Sora e DM Sans no arquivo baixado.
  if ('fonts' in document) await document.fonts.ready;

  try {
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [reportFormat.pdfWidth, reportFormat.pdfHeight],
      compress: true,
    });

    for (const [index, page] of pages.entries()) {
      const canvas = await html2canvas(page, {
        scale: 1,
        backgroundColor: '#FFFFFF',
        allowTaint: false,
        useCORS: true,
        logging: false,
        width: reportFormat.pixelWidth,
        height: reportFormat.pixelHeight,
        windowWidth: reportFormat.pixelWidth,
        windowHeight: reportFormat.pixelHeight,
        scrollX: 0,
        scrollY: 0,
      });

      if (index > 0) pdf.addPage([reportFormat.pdfWidth, reportFormat.pdfHeight], 'portrait');
      pdf.addImage(
        canvas.toDataURL('image/jpeg', 0.95),
        'JPEG',
        0,
        0,
        reportFormat.pdfWidth,
        reportFormat.pdfHeight,
        `dashboard-page-${index + 1}`,
        'FAST',
      );
    }

    pdf.save(fileName);
  } finally {
    // Remove a cópia mesmo se uma página falhar para não deixar elementos invisíveis acumulados no DOM.
    captureSource.remove();
  }
};
